import axios from 'axios'
import {FeatureConfig} from '@/shared/constants/featuresFlag'
import {attachAuthInterceptor} from '@/shared/api/interceptors/authInterceptor'
import {attachErrorInterceptor} from '@/shared/api/interceptors/errorInterceptor'
import {HTTP_METHODS} from '@/shared/constants/httpMethods'
import {API_VERSION, Constants} from '@/shared/constants/commonConstants'

// Function to create axios instance dynamically
const createApiClient = baseUrlKey => {
  const baseURL = import.meta.env[FeatureConfig[baseUrlKey]] || import.meta.env[FeatureConfig.apiUrl]

  const client = axios.create({baseURL, timeout: 100000})

  // Attach interceptors
  attachAuthInterceptor(client)
  attachErrorInterceptor(client)

  return client
}

// Function to get mock data
const getMockData = async config => {
  if (config?.mockData) {
    return config?.mockData
  }
  if (config?.mockPath) {
    const mockData = await import(/* @vite-ignore */ config.mockPath)
    return mockData.default || {}
  }
  return {}
}

// Defense-in-depth: reject path traversal patterns
const containsPathTraversal = str => typeof str === 'string' && str.includes('..')

// Main request function
export const request = async (config, options = {}, queryParams = '') => {
  // Block path traversal attempts in endpoint or query params
  if (containsPathTraversal(config?.endPoint) || containsPathTraversal(queryParams)) {
    console.error('Blocked potential path traversal attempt')
    return {}
  }

  const method = config?.method || HTTP_METHODS.GET
  let endPoint = ''
  if (config?.growthbookSdkKey) {
    endPoint = `/${config.endPoint}/${import.meta.env[FeatureConfig[config?.growthbookSdkKey]]}`
  } else if (config?.baseUrlKey) {
    endPoint = `/${config.endPoint}`
  } else {
    endPoint = `${config?.version || API_VERSION.V1}/${config.endPoint}`
  }

  // Handle query params
  if (
    (method === HTTP_METHODS.GET || (method === HTTP_METHODS.POST && config?.multipart === Constants.Multipart)) &&
    queryParams
  ) {
    endPoint = `${endPoint}?${queryParams}`
  }

  const defaultOptions = {
    method,
    url: endPoint,
    headers: {'Content-Type': config?.contentType || 'application/json', ...config?.headers},
    ...options,
  }

  // Dynamically create client based on baseUrlKey or fallback to apiUrl
  const baseUrlKey = config?.baseUrlKey || 'apiUrl' // Default to 'apiUrl' if not provided
  const apiClient = createApiClient(baseUrlKey)

  let response = {}
  try {
    // In mock mode, skip real API calls and return mock data directly
    if (import.meta.env.VITE_USE_MOCK === 'true') {
      return await getMockData(config)
    }
    response = await apiClient(defaultOptions)
  } catch (err) {
    console.error(err)
    if (config?.throwOnError) {
      throw err
    }
    // Mock data must never be served as a silent fallback outside of mock mode
    // (VITE_USE_MOCK !== 'true'), otherwise real failures (auth/CORS/404/etc.)
    // would be masked by fake data on qa/prod environments.
    return {}
  }
  return response?.data
}
